// Single source of truth for everything money: plan prices, the purchasable
// module catalogue, VAT, dunning thresholds, and invoice numbering. Consumed
// by the API (subscription amounts, invoice creation) and both portals
// (upsell page, billing screens) — never copy a price out of this file.
import type { BillingCadence } from "@cms/types";

/** Monthly price per plan tier, in pence, excluding VAT. */
export const PLAN_MONTHLY_PENCE: Record<string, number> = {
  launch: 5900,
  growth: 8900,
  bespoke: 32900,
};

/** Annual price per plan tier, in pence, excluding VAT (two months free vs monthly). */
export const PLAN_ANNUAL_PENCE: Record<string, number> = {
  launch: 59000,
  growth: 89000,
  bespoke: 329000,
};

/** Months covered by one charge at the given cadence. */
export function cadenceMonths(cadence: BillingCadence): number {
  return cadence === "annual" ? 12 : 1;
}

/**
 * A plan's per-charge price at the given cadence, pence excluding VAT;
 * undefined for unknown tiers. Annual uses the discounted annual rate.
 */
export function planPencePerCharge(plan: string, cadence: BillingCadence): number | undefined {
  return cadence === "annual" ? PLAN_ANNUAL_PENCE[plan] : PLAN_MONTHLY_PENCE[plan];
}

/**
 * A module's per-charge price at the given cadence, pence excluding VAT.
 * Modules have no annual discount — annual is simply 12 months up front.
 */
export function modulePencePerCharge(monthlyPence: number, cadence: BillingCadence): number {
  return monthlyPence * cadenceMonths(cadence);
}

export interface ModuleDefinition {
  slug: string;
  /** Full admin-facing name. */
  name: string;
  /** Short client-portal label (sidebar, upsell cards). */
  label: string;
  /** Monthly price in pence, excluding VAT. */
  monthly_pence: number;
  description: string;
}

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  {
    slug: "restaurant_menu",
    name: "Restaurant menu",
    label: "Menu",
    monthly_pence: 2500,
    description:
      "A dedicated menu manager — categories, dishes, prices and allergens — with a QR code for tables.",
  },
  {
    slug: "booking",
    name: "Bookings",
    label: "Bookings",
    monthly_pence: 2900,
    description:
      "Slot-based table or appointment booking with email confirmations, straight from your site.",
  },
  {
    slug: "posts",
    name: "Blog",
    label: "Blog",
    monthly_pence: 0,
    description:
      "A blog for your site — write posts with the page editor, organise them into categories, and publish with full SEO control.",
  },
  {
    slug: "events",
    name: "Events",
    label: "Events",
    monthly_pence: 1500,
    description:
      "Publish one-off and repeating events — with images, times and locations — shown live on your site the moment you save.",
  },
  {
    slug: "sla",
    name: "Priority support SLA",
    label: "Support",
    monthly_pence: 4900,
    description:
      "Done-for-you content updates with a guaranteed 24-hour turnaround — up to 4 a month — plus a quarterly strategy call.",
  },
];

/** SLA module: done-for-you updates included per calendar month. */
export const SLA_MONTHLY_UPDATE_LIMIT = 4;

export function moduleDefinition(slug: unknown): ModuleDefinition | null {
  return MODULE_DEFINITIONS.find((def) => def.slug === slug) ?? null;
}

// Sanity ceiling for adjustment amounts (£2,000) — a fat-fingered one-off
// charge, discount or override should fail validation, not reach the bank.
export const MAX_ADJUSTMENT_PENCE = 200_000;

// --- VAT ---
// Prices exclude VAT. The business is not VAT registered yet, so the platform
// rate defaults to 0% — once registered, set the API's VAT_RATE_PERCENT var
// (e.g. "20") and every new invoice and subscription amount picks it up.
// Rates are handled in basis points (20% = 2000 bps) so arithmetic stays
// integer-exact; the rate charged is snapshotted onto each invoice.

export const DEFAULT_VAT_RATE_BPS = 0;

/** Parse a percent string ("20", "12.5") to basis points; invalid → 0. */
export function parseVatRateBps(raw: string | undefined | null): number {
  if (!raw) return DEFAULT_VAT_RATE_BPS;
  const percent = Number.parseFloat(raw);
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) return DEFAULT_VAT_RATE_BPS;
  return Math.round(percent * 100);
}

/** VAT amount in pence for a subtotal, rounded to the nearest penny. */
export function vatPenceFor(subtotalPence: number, vatRateBps: number): number {
  return Math.round((subtotalPence * vatRateBps) / 10000);
}

// --- Dunning / lifecycle thresholds ---
// Days after the first failed payment (tenants.billing_past_due_since).
// Deliberately conservative placeholders — tune once real dunning data exists.
// Suspension freezes the client's own portal access only; admins can still
// act-as, and the live site stays up.

export const DUNNING_REMINDER_AFTER_DAYS = 7;
export const SUSPEND_AFTER_DAYS = 14;

// --- Invoice numbering ---
// Placeholder format pending final legal/accounting requirements:
// PGS-<year>-<zero-padded sequence>, sequence resetting each year.

export function formatInvoiceNumber(year: number, sequence: number): string {
  return `PGS-${year}-${String(sequence).padStart(5, "0")}`;
}
